import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchTasks, createTask, fetchCategories } from '../store/slices/tasksSlice';
import { fetchOrganizations } from '../store/slices/organizationsSlice';
import { tasksAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { Loading, EmptyState, Pagination, ConfirmDialog } from '../components/common';
import { formatDate, formatTimeAgo } from '../utils/formatters';
import { TASK_TYPES, TASK_PRIORITIES, TASK_STATUSES } from '../utils/constants';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

const Tasks = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tasks, categories, loading, pagination } = useSelector((state) => state.tasks);
  const { organizations } = useSelector((state) => state.organizations);
  const { user } = useSelector((state) => state.auth);
  
  const [showModal, setShowModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    priority: searchParams.get('priority') || '',
    category: searchParams.get('category') || '',
    search: searchParams.get('search') || '',
    is_overdue: searchParams.get('is_overdue') || '',
    page: parseInt(searchParams.get('page')) || 1,
  });
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    task_type: 'regular',
    priority: 'medium',
    deadline: '',
    is_for_all: false,
    organization_ids: [],
    attachments_required: false,
  });

  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    code: '',
    description: '',
    order: 0,
  });

  useEffect(() => {
    dispatch(fetchCategories());
    dispatch(fetchOrganizations());
  }, [dispatch]);

  useEffect(() => {
    const params = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params[key] = value;
    });
    dispatch(fetchTasks(params));
    
    const newSearchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && key !== 'page') newSearchParams.set(key, value);
    });
    setSearchParams(newSearchParams);
  }, [dispatch, filters, setSearchParams]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await dispatch(createTask(formData)).unwrap();
      toast.success('Topshiriq yaratildi!');
      setShowModal(false);
      setFormData({
        title: '', description: '', category: '', task_type: 'regular',
        priority: 'medium', deadline: '', is_for_all: false, organization_ids: [], attachments_required: false,
      });
      dispatch(fetchTasks(filters));
    } catch (error) {
      toast.error(error?.title?.[0] || 'Xatolik yuz berdi');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Category CRUD
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await tasksAPI.updateCategory(selectedCategory.id, categoryFormData);
        toast.success('Kategoriya yangilandi!');
      } else {
        await tasksAPI.createCategory(categoryFormData);
        toast.success('Kategoriya yaratildi!');
      }
      setShowCategoryModal(false);
      resetCategoryForm();
      dispatch(fetchCategories());
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const handleEditCategory = (cat) => {
    setSelectedCategory(cat);
    setCategoryFormData({
      name: cat.name,
      code: cat.code || '',
      description: cat.description || '',
      order: cat.order || 0,
    });
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async () => {
    try {
      await tasksAPI.deleteCategory(selectedCategory.id);
      toast.success('Kategoriya o\'chirildi!');
      setShowDeleteCategoryModal(false);
      setSelectedCategory(null);
      dispatch(fetchCategories());
    } catch (error) {
      toast.error('Xatolik yuz berdi');
    }
  };

  const resetCategoryForm = () => {
    setSelectedCategory(null);
    setCategoryFormData({ name: '', code: '', description: '', order: 0 });
  };

  const getStatusBadge = (task) => {
    if (task.is_overdue) return 'badge-danger';
    const colors = { completed: 'badge-success', completed_late: 'badge-success', new: 'badge-info', in_progress: 'badge-warning', pending: 'badge-warning', rejected: 'badge-danger', not_completed: 'badge-danger' };
    return colors[task.status] || 'badge-gray';
  };

  const getPriorityBadge = (priority) => {
    const colors = { urgent: 'badge-danger', high: 'badge-warning', medium: 'badge-info', low: 'badge-gray' };
    return colors[priority] || 'badge-gray';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Topshiriqlar</h1>
          <p className="text-gray-500">Jami {pagination.count || tasks.length} ta topshiriq</p>
        </div>
        {user?.role === 'admin' && (
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <PlusIcon className="w-5 h-5" />
            Yangi topshiriq
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Qidirish..."
                className="input pl-10"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
          <select className="select w-auto min-w-[150px]" value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
            <option value="">Barcha holatlar</option>
            {Object.entries(TASK_STATUSES).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
          <select className="select w-auto min-w-[130px]" value={filters.priority} onChange={(e) => handleFilterChange('priority', e.target.value)}>
            <option value="">Barcha muhimlik</option>
            {Object.entries(TASK_PRIORITIES).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
          <select className="select w-auto min-w-[150px]" value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)}>
            <option value="">Barcha kategoriyalar</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          {user?.role === 'admin' && (
            <button onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }} className="btn-outline" title="Kategoriyalarni boshqarish">
              <Cog6ToothIcon className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Admin uchun kategoriyalar ro'yxati */}
        {user?.role === 'admin' && categories.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-600">Kategoriyalar:</p>
              <button onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }} className="text-sm text-blue-600 hover:underline">
                + Yangi kategoriya
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <div key={cat.id} className="flex items-center gap-1 bg-gray-100 rounded-lg px-3 py-1.5">
                  <span className="text-sm text-gray-700">{cat.name}</span>
                  <button onClick={() => handleEditCategory(cat)} className="p-0.5 hover:bg-gray-200 rounded">
                    <PencilIcon className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                  <button onClick={() => { setSelectedCategory(cat); setShowDeleteCategoryModal(true); }} className="p-0.5 hover:bg-gray-200 rounded">
                    <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tasks Table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <Loading />
        ) : tasks.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Sarlavha</th>
                    <th className="table-header hidden md:table-cell">Kategoriya</th>
                    <th className="table-header">Muddat</th>
                    <th className="table-header hidden sm:table-cell">Muhimlik</th>
                    <th className="table-header">Holat</th>
                    <th className="table-header hidden lg:table-cell">Ijro</th>
                    <th className="table-header w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="table-cell">
                        <Link to={`/tasks/${task.id}`} className="text-blue-600 hover:underline font-medium">
                          {task.title}
                        </Link>
                        <p className="text-xs text-gray-500 md:hidden mt-1">{task.category_name}</p>
                      </td>
                      <td className="table-cell hidden md:table-cell text-gray-500">{task.category_name}</td>
                      <td className="table-cell">
                        <span className={task.is_overdue ? 'text-red-600 font-medium' : ''}>
                          {formatDate(task.deadline)}
                        </span>
                        <span className="block text-xs text-gray-400">
                          {formatTimeAgo(task.deadline)}
                        </span>
                      </td>
                      <td className="table-cell hidden sm:table-cell">
                        <span className={`badge ${getPriorityBadge(task.priority)}`}>
                          {TASK_PRIORITIES[task.priority]}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${getStatusBadge(task)}`}>
                          {task.is_overdue ? "Muddati o'tgan" : task.status_display}
                        </span>
                      </td>
                      <td className="table-cell hidden lg:table-cell text-gray-500">
                        {task.completed_count}/{task.organizations_count}
                      </td>
                      <td className="table-cell">
                        <Link to={`/tasks/${task.id}`} className="btn-ghost btn-sm">
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination.count > 20 && (
              <div className="p-4 border-t">
                <Pagination
                  currentPage={filters.page}
                  totalPages={Math.ceil(pagination.count / 20)}
                  totalItems={pagination.count}
                  pageSize={20}
                  onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={ClipboardDocumentListIcon}
            title="Topshiriqlar topilmadi"
            description="Filterlarni o'zgartiring yoki yangi topshiriq yarating"
          />
        )}
      </div>

      {/* Create Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Yangi topshiriq" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="input-group">
            <label className="label">Sarlavha *</label>
            <input type="text" className="input" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
          </div>
          <div className="input-group">
            <label className="label">Tavsif *</label>
            <textarea className="textarea" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Kategoriya</label>
              <select className="select" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                <option value="">Tanlang</option>
                {categories.map((cat) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Turi</label>
              <select className="select" value={formData.task_type} onChange={(e) => setFormData({ ...formData, task_type: e.target.value })}>
                {Object.entries(TASK_TYPES).map(([key, value]) => (<option key={key} value={key}>{value}</option>))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Muhimlik</label>
              <select className="select" value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                {Object.entries(TASK_PRIORITIES).map(([key, value]) => (<option key={key} value={key}>{value}</option>))}
              </select>
            </div>
            <div className="input-group">
              <label className="label">Muddat *</label>
              <input type="datetime-local" className="input" value={formData.deadline} onChange={(e) => setFormData({ ...formData, deadline: e.target.value })} required />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.is_for_all} onChange={(e) => setFormData({ ...formData, is_for_all: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm">Barcha OTMlarga yuborish</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.attachments_required} onChange={(e) => setFormData({ ...formData, attachments_required: e.target.checked })} className="w-4 h-4" />
              <span className="text-sm">Fayl talab qilinadi</span>
            </label>
          </div>
          {!formData.is_for_all && (
            <div className="input-group">
              <label className="label">Tashkilotlarni tanlang</label>
              <select multiple className="input min-h-[150px]" value={formData.organization_ids} onChange={(e) => setFormData({ ...formData, organization_ids: Array.from(e.target.selectedOptions, o => parseInt(o.value)) })}>
                {organizations.map((org) => (<option key={org.id} value={org.id}>{org.short_name || org.name}</option>))}
              </select>
              <p className="text-xs text-gray-500 mt-1">Ctrl bosib ko'p tanlang</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">Yaratish</button>
          </div>
        </form>
      </Modal>

      {/* Category Modal */}
      <Modal isOpen={showCategoryModal} onClose={() => setShowCategoryModal(false)} title={selectedCategory ? 'Kategoriyani tahrirlash' : 'Yangi kategoriya'} size="md">
        <form onSubmit={handleCategorySubmit} className="space-y-4">
          <div className="input-group">
            <label className="label">Nomi *</label>
            <input type="text" className="input" value={categoryFormData.name} onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })} required placeholder="Masalan: Moliyaviy hisobotlar" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="input-group">
              <label className="label">Kod</label>
              <input type="text" className="input" value={categoryFormData.code} onChange={(e) => setCategoryFormData({ ...categoryFormData, code: e.target.value })} placeholder="Masalan: FIN-01" />
            </div>
            <div className="input-group">
              <label className="label">Tartib raqami</label>
              <input type="number" min="0" className="input" value={categoryFormData.order} onChange={(e) => setCategoryFormData({ ...categoryFormData, order: parseInt(e.target.value) || 0 })} />
            </div>
          </div>
          <div className="input-group">
            <label className="label">Tavsif</label>
            <textarea className="textarea" value={categoryFormData.description} onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })} placeholder="Kategoriya haqida qisqacha ma'lumot" />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button type="button" onClick={() => setShowCategoryModal(false)} className="btn-secondary">Bekor qilish</button>
            <button type="submit" className="btn-primary">{selectedCategory ? 'Saqlash' : 'Yaratish'}</button>
          </div>
        </form>
      </Modal>

      {/* Delete Category Confirm */}
      <ConfirmDialog 
        isOpen={showDeleteCategoryModal} 
        onClose={() => setShowDeleteCategoryModal(false)} 
        onConfirm={handleDeleteCategory}
        title="Kategoriyani o'chirish" 
        message={`"${selectedCategory?.name}" kategoriyasini o'chirishni xohlaysizmi? Bu kategoriyaga tegishli topshiriqlar ham ta'sirlanishi mumkin.`} 
        confirmText="O'chirish" 
      />
    </div>
  );
};

export default Tasks;
